import React, { useState, useEffect, useCallback } from "react";
import {
  Layout, Table, Button, Space, Descriptions, Tag, message, Row, Col, Modal, Breadcrumb, Timeline
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { ArrowLeftOutlined, ShoppingCartOutlined, EditOutlined, DeleteOutlined, PrinterOutlined, HomeOutlined, HistoryOutlined, ClockCircleOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import moment from "moment";
import "../../../styles/fluidDesign.css";
import { getStatusColor } from "../../../utils/statusHelper";
import PRPreviewModal from "../../../components/common/PRPreviewModal";

const { Content } = Layout;

const ViewPR = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [poMaster, setPoMaster] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);

  // Permission check using localStorage (current project pattern)
  const isDisabled = (permName) => {
    const value = localStorage.getItem(`USERPERMISSION_${permName}`);
    if (value === null) return true;
    return value !== 'false';
  };

  const isDraft = poMaster && poMaster.pr_status === 'Draft';

  const handleDeletePR = () => {
    Modal.confirm({
      title: 'Delete Purchase Request',
      content: `Are you sure you want to delete ${poMaster?.pr_ref}? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const res = await api.post("/inventory/pr/delete.php", { pr_id: id });
          if (res.data && res.data.status === "success") {
            message.success(res.data.message || "Purchase Request deleted successfully.");
            navigate("/inventory-purchase-requests");
          } else {
            message.error(res.data.message || "Failed to delete Purchase Request.");
          }
        } catch (error) {
          message.error("Error deleting Purchase Request.");
        }
      }
    });
  };

  const fetchPRDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inventory/pr/get.php?id=${id}`);
      if (res.data && res.data.status === "success") {
        setPoMaster(res.data.master);
        setPoItems(res.data.items || []);
      } else {
        message.error("Purchase Request not found.");
      }
    } catch (error) {
      message.error("Failed to load Purchase Request details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPRDetails();
  }, [fetchPRDetails]);

  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  // Build approval history steps in chronological order (Step 1..N)
  const buildApprovalHistory = () => {
    if (!poMaster) return [];
    const status = poMaster.pr_status;
    const isPending = status === "Pending" || status === "Pending Approval";
    const isDecided = ["Approved", "Rejected", "Requested", "Partially Received", "Received"].includes(status);

    const steps = [];

    // Step 1 - Always: Purchase Request Created
    steps.push({
      key: "created",
      stepNo: 1,
      title: "Purchase Request Created",
      state: "done",
      color: "green",
      user: poMaster.creator_name || null,
      date: poMaster.created_dt || null,
      comment: poMaster.ipo_note || null
    });

    // Step 2 - Awaiting Review
    steps.push({
      key: "review",
      stepNo: 2,
      title: "Awaiting Review",
      state: isPending ? "current" : (isDecided ? "done" : "todo"),
      color: isPending ? "blue" : (isDecided ? "green" : "gray"),
      user: null,
      date: null,
      comment: null
    });

    // Step 3 - Decision (Approved / Rejected) - only once decided
    if (isDecided) {
      const approved = status !== "Rejected";
      steps.push({
        key: "decision",
        stepNo: 3,
        title: approved ? "Approved" : "Rejected",
        state: "done",
        color: approved ? "green" : "red",
        user: poMaster.approver_name || null,
        date: poMaster.approved_dt || null,
        comment: poMaster.approved_comment || null
      });
    }

    return steps;
  };

  const renderApprovalHistory = () => {
    const steps = buildApprovalHistory();
    // Reverse so the most recent step appears on top (matches previous project layout)
    const reversed = [...steps].reverse();
    return (
      <Timeline
        items={reversed.map((s) => ({
          color: s.color,
          dot: s.state === "current" ? <ClockCircleOutlined style={{ color: "#1890ff", fontSize: 16 }} /> : undefined,
          children: (
            <div style={{ paddingBottom: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ fontSize: 14, color: "var(--fluid-text)" }}>{s.title}</strong>
                <Tag color={s.state === "current" ? "processing" : s.color}>{`Step ${s.stepNo}`}</Tag>
                {s.state === "current" && <Tag color="gold">In Progress</Tag>}
              </div>
              {s.user && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ color: "#888" }}>By: </span>
                  <span style={{ fontWeight: 500, color: "var(--fluid-text)" }}>{s.user}</span>
                </div>
              )}
              {s.date && (
                <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                  {moment(s.date).format("YYYY-MM-DD hh:mm:ss A")}
                </div>
              )}
              {s.comment && (
                <div style={{
                  marginTop: 6,
                  padding: "6px 10px",
                  background: "#fffbe6",
                  border: "1px solid #ffe58f",
                  borderRadius: 6,
                  fontSize: 12.5,
                  color: "#ad6800",
                  lineHeight: 1.5
                }}>
                  "{s.comment}"
                </div>
              )}
            </div>
          )
        }))}
      />
    );
  };

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
                          <Breadcrumb
                  className="fluid-breadcrumb"
                  items={[
                      { href: '/dash', title: <HomeOutlined /> },
                      { href: '/inventory-items', title: 'Inventory' },
                      { title: 'View PR' },
                  ]}
              />
            <h1 className="fluid-page-title">
              <ShoppingCartOutlined /> PR: {poMaster ? poMaster.pr_ref : "Loading..."}
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => setPreviewVisible(true)}
                className="fluid-btn-visible"
              >
                Print
              </Button>
              {isDraft && !isDisabled('pr_create') && (
                <>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/inventory/pr/${id}/items`)}
                    className="fluid-btn-visible"
                  >
                    Edit Items
                  </Button>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDeletePR}
                    style={{ background: "#fff", borderColor: "#ff4d4f", color: "#ff4d4f" }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content">
        {poMaster ? (
          <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <div className="fluid-card">
              <div className="fluid-card-header">
                <h3 className="fluid-card-title">
                  <ShoppingCartOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                  PR Overview
                </h3>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/inventory-purchase-requests")}>
                  Back to List
                </Button>
              </div>
              <Descriptions bordered column={{ xxl: 4, xl: 4, lg: 2, md: 2, sm: 1, xs: 1 }} size="small">
                <Descriptions.Item label="PR Reference"><strong>{poMaster.pr_ref}</strong></Descriptions.Item>
                <Descriptions.Item label="Supplier">{poMaster.supplier_name}</Descriptions.Item>
                <Descriptions.Item label="PR Date">{moment(poMaster.pr_date).format("YYYY-MM-DD")}</Descriptions.Item>
                <Descriptions.Item label="Expected Delivery">{poMaster.delivery_expected_date ? moment(poMaster.delivery_expected_date).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                <Descriptions.Item label="Status">{renderStatusTag(poMaster.pr_status)}</Descriptions.Item>
                <Descriptions.Item label="Created By">{poMaster.creator_name || "-"}</Descriptions.Item>
                <Descriptions.Item label="Approver">{poMaster.approver_name || "-"}</Descriptions.Item>
                <Descriptions.Item label="Approved Date">{poMaster.approved_dt ? moment(poMaster.approved_dt).format("YYYY-MM-DD HH:mm") : "-"}</Descriptions.Item>
                <Descriptions.Item label="Approval Feedback" span={{ xxl: 4, xl: 4, lg: 2, md: 2, sm: 1, xs: 1 }}>{poMaster.approved_comment || "No comment"}</Descriptions.Item>
                <Descriptions.Item label="PR Note" span={{ xxl: 4, xl: 4, lg: 2, md: 2, sm: 1, xs: 1 }}>{poMaster.ipo_note || "-"}</Descriptions.Item>
              </Descriptions>
            </div>

            <div className="fluid-card">
              <div className="fluid-card-header">
                <h3 className="fluid-card-title">Requested Items</h3>
              </div>
              <div className="fluid-table">
                <Table
                  dataSource={poItems}
                  rowKey="pritem_id"
                  pagination={false}
                  bordered
                  size="small"
                  loading={loading}
                  columns={[
                    { title: "Item Code", dataIndex: "inv_code", key: "inv_code", render: (val) => val || "-" },
                    {
                      title: "Item Description",
                      key: "inv_desc",
                      render: (_, record) => (
                        <div>
                          <strong>{record.inv_desc || "-"}</strong>
                          {record.item_note && (
                            <div style={{ fontSize: "12px", color: "var(--fluid-harvest)", marginTop: 2, fontWeight: "500" }}>
                              Note: {record.item_note}
                            </div>
                          )}
                        </div>
                      )
                    },
                    { title: "Order Qty", dataIndex: "order_qty", key: "order_qty", align: "right", render: (qty) => <strong>{Number(qty).toFixed(2)}</strong> },
                    { title: "Received Qty", dataIndex: "received_qty", key: "received_qty", align: "right", render: (qty) => <span style={{ color: "#52c41a", fontWeight: "600" }}>{Number(qty).toFixed(2)}</span> },
                    { title: "Unit Price", dataIndex: "unit_price", key: "unit_price", align: "right", render: (val) => `LKR ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
                    {
                      title: "Sub Total",
                      dataIndex: "item_subtotal",
                      key: "item_subtotal",
                      align: "right",
                      render: (val) => <strong>LKR {Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                    }
                  ]}
                />
              </div>

              <div style={{ marginTop: 24, textAlign: "right" }}>
                <Descriptions column={1} size="small" bordered style={{ width: 340, display: "inline-block" }}>
                  <Descriptions.Item label="Sub Total">LKR {Number(poMaster.pr_sub_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                  <Descriptions.Item label="Tax">LKR {Number(poMaster.pr_total_tax).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                  <Descriptions.Item label="Discount">LKR {Number(poMaster.pr_total_discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                  <Descriptions.Item label="Grand Total">
                    <strong style={{ fontSize: "16px", color: "var(--fluid-primary)" }}>LKR {Number(poMaster.pr_final_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </div>

            <div className="fluid-card">
              <div className="fluid-card-header">
                <h3 className="fluid-card-title">
                  <HistoryOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                  Approval History
                </h3>
              </div>
              <div style={{ padding: "4px 8px" }}>
                {renderApprovalHistory()}
              </div>
            </div>
          </Space>
        ) : (
          <div className="fluid-card" style={{ textAlign: 'center', padding: 40 }}>
            {loading ? "Loading..." : "Purchase Request not found"}
          </div>
        )}
      </Content>
      <Footerdiv />

      {previewVisible && (
        <PRPreviewModal
          prId={id}
          visible={previewVisible}
          onCancel={() => setPreviewVisible(false)}
        />
      )}
    </Layout>
  );
};

export default ViewPR;
